import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InformationGrid from "components/shared/InformationGrid";
import LongTextBlock from "components/shared/LongTextBlock";
import MarkAsCheckbox from "components/shared/MarkAsCheckbox";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBeneficiaryTags, deleteBeneficiaryTag, markBeneficiaryProfileComplete } from "store/beneficiaryStore";
import type { IStatementTemplate } from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import PaymentBlock from "../PaymentBlock";
import PortalAccountSection from "../PortalAccountSection";

interface IGeneralInfoTab {
    usesBeneficiaryProfileCompleteCheck: boolean;
    beneficiar: IBeneficiar;
    data: {
        personalInfo: Record<string, string>;
        addressInfo: Record<string, string>;
        accountingInfo: Record<string, string>;
        profileInfo: Record<string, string>;
        beneficiaryProfileInfos: Record<string, string>;
    };
    textAreaFields: { title: string; text: any }[];
    maskSensitiveInfo: Record<string, boolean>;
    setMaskSensitiveInfo: Dispatch<SetStateAction<Record<string, boolean>>>;
    setModalVariant: Dispatch<SetStateAction<"delete" | "create" | "edit" | "deleteRep" | "access" | null>>;
    isLoading?: boolean;
}

/**
 * General info tab on beneficiary page.
 */
const GeneralInfoTab = ({
    usesBeneficiaryProfileCompleteCheck,
    beneficiar,
    data,
    textAreaFields,
    maskSensitiveInfo,
    setMaskSensitiveInfo,
    setModalVariant,
    isLoading = false,
}: IGeneralInfoTab) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);

    const { profileInfo, addressInfo, accountingInfo, personalInfo, beneficiaryProfileInfos } = data;

    const { hasPermissions } = usePermissions();

    const { getCompanySettings } = useCompanySettings();
    const acceptedPaymentMethods: string[] =
        getCompanySettings("accepted_payment_methods")?.content ?? '["rib","paypal","check"]';
    const companyAcceptedPayments = getCompanySettings("accepted_payment_methods");

    const beneficiarStatementTemplate = useMemo(
        () =>
            partnerStatements.find(
                (statement: IStatementTemplate) => statement.id == beneficiar.customStatementTemplateId,
            ),
        [beneficiar],
    );

    const onGoogleSheetNavigate = () => {
        window.open(
            `https://docs.google.com/spreadsheets/d/${beneficiarStatementTemplate?.data.spreadsheetId}/edit#gid=${beneficiarStatementTemplate?.data.sheetId}`,
            "_blank",
        );
    };

    const handleMarkProfileAsCompleted = async (isProfileComplete: boolean) => {
        if (!hasPermissions(["beneficiary:edit-profile-complete"])) return;
        try {
            await dispatch(
                markBeneficiaryProfileComplete({ beneficiaryId: beneficiar.id, isProfileComplete, toast: true }),
            );
            if (isProfileComplete) {
                const tag = {
                    name: t("Beneficiars.ProfileComplete"),
                    color: 1,
                };
                dispatch(createBeneficiaryTags({ beneficiaryId: beneficiar.id, tags: tag, toast: false }));
            } else {
                const profileCompleteTag = beneficiar.tags?.find(
                    (tag) => tag.name === t("Beneficiars.ProfileComplete"),
                );
                if (!profileCompleteTag) return;
                dispatch(
                    deleteBeneficiaryTag({ beneficiaryId: beneficiar.id, tagId: profileCompleteTag.id, toast: false }),
                );
            }
        } catch {
            /* empty */
        }
    };

    return (
        <>
            {/** Check is profile complete */}
            {usesBeneficiaryProfileCompleteCheck && !isLoading && (
                <div className="beneficiar-mark-as-complete">
                    <MarkAsCheckbox
                        checked={!!beneficiar.isProfileComplete}
                        handleChange={handleMarkProfileAsCompleted}
                        label={t("Beneficiars.MarkAsComplete")}
                        checkedLabel={t("Beneficiars.ProfileComplete")}
                        disabled={!hasPermissions(["beneficiary:edit-profile-complete"])}
                    />
                </div>
            )}
            <div className="beneficiar-content">
                <InformationGrid
                    data={personalInfo}
                    title={t("NewBeneficiary.PersonalInformation")}
                    maskedInfo={maskSensitiveInfo}
                    setMaskedInfo={(data: any) => setMaskSensitiveInfo(data)}
                    isLoading={isLoading}
                />
                <InformationGrid data={addressInfo} title={t("Beneficiars.Address")} isLoading={isLoading} />
                {!isLoading && <PortalAccountSection beneficiary={beneficiar} setModalVariant={setModalVariant} />}
                <InformationGrid
                    data={profileInfo}
                    title={t("NewBeneficiary.Profile")}
                    maskedInfo={maskSensitiveInfo}
                    setMaskedInfo={(data: any) => setMaskSensitiveInfo(data)}
                    isLoading={isLoading}
                />
                {!isLoading && (
                    <PaymentBlock
                        beneficiary={beneficiar}
                        acceptedPaymentMethods={acceptedPaymentMethods}
                        companyAcceptedPayments={companyAcceptedPayments}
                        maskSensitiveInfo={maskSensitiveInfo}
                        setMaskSensitiveInfo={setMaskSensitiveInfo}
                    />
                )}

                <InformationGrid data={accountingInfo} title={t("Beneficiars.AccountingInfo")} isLoading={isLoading} />
                {!isLoading && beneficiar?.customStatementTemplateId && (
                    <div className="beneficiar-custom-statement">
                        <div className="beneficiar-custom-statement-content">
                            <div className="beneficiar-custom-statement-title">
                                {t("PartnerStatements.CustomStatement")}
                                <div className="beneficiar-view-statement" onClick={() => onGoogleSheetNavigate()}>
                                    <OpenInNewOutlinedIcon sx={{ height: "14px", width: "14px" }} />
                                    {t("PartnerStatements.ViewTemplate")}
                                </div>
                            </div>
                            <div className="beneficiar-custom-statement-text">
                                {t("PartnerStatements.BeneficiarCustomStatementInfo")}
                            </div>
                        </div>
                    </div>
                )}
                {textAreaFields.map((field) => (
                    <LongTextBlock
                        text={field.text}
                        title={field.title}
                        key={`title.longstring.${field.title.trim()}`}
                        isLoading={isLoading}
                    />
                ))}
                <InformationGrid data={beneficiaryProfileInfos} title={t("Titles.DateInfo")} isLoading={isLoading} />
            </div>
        </>
    );
};

export default GeneralInfoTab;
